import { readAllBytes, type Bridge } from '@casa/bridge';
import { openCloudSecrets, sealCloudSecrets } from './cloud_secrets.ts';

// The dashboards the Tilez Hub keeps (requirements S-1, S-1a, S-2 and S-5 in
// docs/specs/dashboard-editor.md; the server's dashboards module). How the app reaches the server
// depends on the host:
// - In a browser, the page came from the server: it calls the server on its own origin with its
//   session cookie. Not through the browser bridge, which serves the LAN and refuses the server's
//   own address (decision §11, 2026-10-16).
// - In a native shell, the server is the one the user paired the app with: requests go through the
//   bridge's `net.http`, like every other request, with the token the server gave at pairing. The
//   address and the token are kept in the app's storage; the token is never logged or shown.

/** The server and the app's token, in a native shell. */
export const SERVER_PATH = 'casa-server.json';
/** The same for Tilez Cloud, which a device is paired with beside its Tilez Hub (settings ST-10). */
export const CLOUD_PATH = 'tilez-cloud.json';
/** Tilez Cloud's address, as the shells know it for their updates (app-updates QAU-3). */
export const CASA_CLOUD_URL = 'https://casa-cloud.app';
/** For checks against a tilez-cloud run locally: set from developer tools only, as the shells' CASA_UPDATES_SERVER is for updates. */
const DEV_CLOUD_URL_KEY = 'casa.dev.cloudUrl';

/** Where Tilez Cloud is: `casa-cloud.app`, unless a check pointed this page at another (`localStorage['casa.dev.cloudUrl']`). */
export function casaCloudUrl() : string {
    try {
        return localStorage.getItem(DEV_CLOUD_URL_KEY) ?? CASA_CLOUD_URL;
    } catch {
        return CASA_CLOUD_URL;
    }
}

/** The two servers a device may be paired with at once: its Tilez Hub (`casa`, at home) and Tilez Cloud (`cloud`). */
export type ServerStore = 'casa' | 'cloud';

const pairingPath = (store: ServerStore) : string => (store === 'cloud' ? CLOUD_PATH : SERVER_PATH);

const TIMEOUT_MS = 15_000;

export interface ServerDashboard {
    name: string;
    version: string;
    updated: string;
    size: number;
    /** The schema version the file is in; null when it says none, absent from a server before A-12. */
    schemaVersion?: number | null;
}

export type ServerLink = { kind: 'page' } | { kind: 'app'; url: string; token: string };

/** What a server says it is and offers (`GET /api/server`, CS-3). Values this app does not know are kept, and shown as they are. */
export interface ServerInfo {
    /** `tilez-pico` (the Pico store), `tilez-hub` (a home server) or `tilez-cloud` (the cloud): CS-1, CS-3; before 2026-10-30 `tiny-store`, `local-admin`, `public-server`. */
    kind: string;
    version: string;
    /** `dashboards`, `admin`, `secrets`, `drivers`, `bridge`. */
    offers: string[];
    /** How a device gets in: `code` (a pairing code), `open` (no code). */
    signIn: string[];
}

export class ServerError extends Error {
    override name = 'ServerError';
    readonly status: number;
    readonly issues: readonly string[];

    constructor(message: string, status = 0, issues: readonly string[] = []) {
        super(message);
        this.status = status;
        this.issues = issues;
    }
}

/** The name a device pairs as: "Tilez on Windows", "Tilez on iPad", "Tilez on SM-X710" (an Android model). */
export function deviceName(info: { platform: string; model: string }) : string {
    const desktops: Record<string, string> = { win32: 'Windows', darwin: 'Mac', linux: 'Linux' };
    const desktop = desktops[info.platform];

    if (desktop) {
        return `Tilez on ${desktop}`;
    }

    if (info.platform === 'ios') {
        return `Tilez on ${/^iPad/i.test(info.model) ? 'iPad' : /^iPhone/i.test(info.model) ? 'iPhone' : 'iOS'}`;
    }

    return `Tilez on ${info.model || info.platform}`.slice(0, 60);
}

/** In a browser no shell announces itself: the page came from the Tilez Hub. */
export const isServerPage = () : boolean => (globalThis as { casaHost?: unknown }).casaHost === undefined;

/**
 * How this device reaches its Tilez Hub, or Tilez Cloud (`store`); undefined when it has not been
 * paired with it. A browser's server is the one that served the page, and it has no Tilez Cloud.
 */
export async function serverLink(bridge: Bridge, store: ServerStore = 'casa') : Promise<ServerLink | undefined> {
    if (isServerPage()) {
        return store === 'casa' ? { kind: 'page' } : undefined;
    }

    const stored = await bridge.storage.read(pairingPath(store));
    if (!stored) {
        return undefined;
    }

    try {
        const parsed = JSON.parse(new TextDecoder().decode(stored)) as Partial<{ url: string; token: string }>;

        return typeof parsed.url === 'string' && typeof parsed.token === 'string' ? { kind: 'app', url: parsed.url, token: parsed.token } : undefined;
    } catch {
        return undefined;
    }
}

/** The server's address for the user: this page's, or the one the app was paired with. */
export function describeServer(link: ServerLink) : string {
    return link.kind === 'page' ? location.origin : link.url;
}

interface Answer {
    status: number;
    etag: string | undefined;
    text: string;
}

export async function call(bridge: Bridge, link: ServerLink, method: string, path: string, body?: string, headers: Record<string, string> = {}) : Promise<Answer> {
    if (link.kind === 'page') {
        const response = await fetch(path, { method, headers, body: body ?? null, credentials: 'same-origin', cache: 'no-store' });

        return { status: response.status, etag: response.headers.get('etag') ?? undefined, text: await response.text() };
    }

    const response = await bridge.net.http.request({
        method,
        url: new URL(path, link.url).href,
        // No token before pairing.
        headers: link.token === '' ? headers : { ...headers, authorization: `Bearer ${link.token}` },
        ...(body === undefined ? {} : { body: new TextEncoder().encode(body) }),
        timeoutMs: TIMEOUT_MS,
    });
    const bytes = await readAllBytes(response.body);

    return { status: response.status, etag: response.headers.etag, text: new TextDecoder().decode(bytes) };
}

function errorOf(answer: Answer, doing: string) : ServerError {
    let message = `${doing}: the server answered ${answer.status}`;
    let issues: string[] = [];
    try {
        const parsed = JSON.parse(answer.text) as { error?: unknown; issues?: unknown };
        if (typeof parsed.error === 'string') {
            message = `${doing}: ${parsed.error}`;
        }
        if (Array.isArray(parsed.issues)) {
            issues = parsed.issues.filter((issue): issue is string => typeof issue === 'string');
        }
    } catch {
        // Not JSON: the status says enough.
    }
    if (answer.status === 401) {
        message = `${doing}: this device is not paired with the Tilez Hub (any more). Pair it again in the settings.`;
    }

    return new ServerError(message, answer.status, issues);
}

const pathOf = (name: string) : string => `/api/dashboards/${encodeURIComponent(name)}`;
const versionOf = (etag: string | undefined) : string => (etag ?? '').replace(/^"|"$/g, '');

/**
 * Pairs this app with a Tilez Hub by a code from it (the server's command line, or its admin page
 * in a browser), and keeps the address and the token. For the native shells.
 */
export async function pairWithServer(bridge: Bridge, url: string, code: string, name: string, store: ServerStore = 'casa') : Promise<void> {
    const base = new URL(url.trim());
    if (base.protocol !== 'https:' && base.protocol !== 'http:') {
        throw new ServerError('The address must start with https:// or http://.');
    }

    const answer = await call(bridge, { kind: 'app', url: base.origin, token: '' }, 'POST', '/api/apps/pair', JSON.stringify({ code: code.trim(), name }), { 'content-type': 'application/json' });
    if (answer.status !== 200) {
        throw errorOf(answer, 'Pairing failed');
    }

    const { token } = JSON.parse(answer.text) as { token: string };
    await bridge.storage.write(pairingPath(store), new TextEncoder().encode(`${JSON.stringify({ url: base.origin, token })}\n`));
}

/**
 * Signs this app in to a Tilez Hub with users (casa-server CS-19: its `/api/server` says
 * `password`), and keeps the address and the device token it gives; the password is not kept.
 */
export async function signInToServer(bridge: Bridge, url: string, user: string, password: string, device: string, store: ServerStore = 'casa') : Promise<void> {
    const base = new URL(url.trim());
    if (base.protocol !== 'https:' && base.protocol !== 'http:') {
        throw new ServerError('The address must start with https:// or http://.');
    }

    const answer = await call(bridge, { kind: 'app', url: base.origin, token: '' }, 'POST', '/api/apps/signin', JSON.stringify({ user: user.trim(), password, device }), { 'content-type': 'application/json' });
    if (answer.status !== 200) {
        throw errorOf(answer, 'Signing in failed');
    }

    const { token } = JSON.parse(answer.text) as { token: string };
    await bridge.storage.write(pairingPath(store), new TextEncoder().encode(`${JSON.stringify({ url: base.origin, token })}\n`));
}

/**
 * Starts a space on Tilez Cloud for this device (cloud-spaces SP-2), or joins the one whose space key
 * (XXXX-XXXX) it has (SP-4), and keeps the pairing as Tilez Cloud's. No code and no account.
 */
export async function enterCloudSpace(bridge: Bridge, how: 'start' | 'join', joinKey: string, name: string) : Promise<void> {
    const path = how === 'start' ? '/api/spaces' : '/api/spaces/join';
    const url = casaCloudUrl();
    const answer = await call(bridge, { kind: 'app', url, token: '' }, 'POST', path, JSON.stringify({ join: joinKey, name }), { 'content-type': 'application/json' });
    if (answer.status !== 200 && answer.status !== 201) {
        throw errorOf(answer, how === 'start' ? 'Starting a space failed' : 'Joining the space failed');
    }

    const { token } = JSON.parse(answer.text) as { token: string };
    await bridge.storage.write(CLOUD_PATH, new TextEncoder().encode(`${JSON.stringify({ url, token })}\n`));
}

/** A device's sign-in with a Tilez Cloud account, as it waits for the account's approval (cloud-accounts AC-13). */
export interface DeviceSignIn {
    deviceCode: string;
    /** XXXX-XXXX, shown on the device. */
    userCode: string;
    /** The account page that approves it, with the code: for a QR code and a link. */
    verifyUrl: string;
    expires: string;
    /** Seconds between asking whether it was approved. */
    interval: number;
}

/** Asks Tilez Cloud to sign this device in with an account: the code to show. */
export async function startDeviceSignIn(bridge: Bridge, name: string) : Promise<DeviceSignIn> {
    const answer = await call(bridge, { kind: 'app', url: casaCloudUrl(), token: '' }, 'POST', '/api/spaces/device-code', JSON.stringify({ name }), { 'content-type': 'application/json' });
    if (answer.status !== 200) {
        throw errorOf(answer, 'Signing in failed');
    }

    return JSON.parse(answer.text) as DeviceSignIn;
}

/**
 * Whether the account approved this device yet. Approved, the device keeps the pairing as Tilez
 * Cloud's, in the space chosen; `isNew` when that space is new, its keys still to be made here.
 */
export async function pollDeviceSignIn(bridge: Bridge, deviceCode: string) : Promise<{ status: 'waiting' } | { status: 'approved'; isNew: boolean } | { status: 'denied' | 'expired'; message: string }> {
    const url = casaCloudUrl();
    const answer = await call(bridge, { kind: 'app', url, token: '' }, 'POST', '/api/spaces/device-token', JSON.stringify({ deviceCode }), { 'content-type': 'application/json' });
    const body = JSON.parse(answer.text || '{}') as { status?: string; token?: string; isNew?: boolean; error?: string };

    if (answer.status === 202) {
        return { status: 'waiting' };
    }

    if (answer.status === 200 && typeof body.token === 'string') {
        await bridge.storage.write(CLOUD_PATH, new TextEncoder().encode(`${JSON.stringify({ url, token: body.token })}\n`));

        return { status: 'approved', isNew: body.isNew === true };
    }

    if (answer.status === 403 || answer.status === 410) {
        return { status: answer.status === 403 ? 'denied' : 'expired', message: body.error ?? 'Start again.' };
    }

    throw errorOf(answer, 'Signing in failed');
}

/** A code that links this device's space on Tilez Cloud to an account (AC-11), and where to type it. */
export async function makeLinkCode(bridge: Bridge, link: ServerLink) : Promise<{ code: string; expires: string; url: string }> {
    const answer = await call(bridge, link, 'POST', '/api/spaces/link-code');
    if (answer.status !== 200) {
        throw errorOf(answer, 'Making a code failed');
    }

    return JSON.parse(answer.text) as { code: string; expires: string; url: string };
}

/** Gives the space a new space key (SP-6). */
export async function setSpaceJoinKey(bridge: Bridge, link: ServerLink, joinKey: string) : Promise<void> {
    const answer = await call(bridge, link, 'PUT', '/api/spaces/join-key', JSON.stringify({ join: joinKey }), { 'content-type': 'application/json' });
    if (answer.status !== 200) {
        throw errorOf(answer, 'Keeping the space key failed');
    }
}

/** Deletes this device's space on Tilez Cloud with everything in it (SP-8), and forgets the pairing. */
export async function deleteCloudSpace(bridge: Bridge, link: ServerLink) : Promise<void> {
    const answer = await call(bridge, link, 'DELETE', '/api/spaces/current');
    if (answer.status !== 200) {
        throw errorOf(answer, 'Deleting the space failed');
    }

    await bridge.storage.remove(CLOUD_PATH);
}

/** This device as the server knows it: its pairing, and the user it signed in as, if any. */
export interface ServerSession {
    paired: boolean;
    client?: PairedClient;
    user?: { name: string; role: 'admin' | 'reader' };
}

export async function readSession(bridge: Bridge, link: ServerLink) : Promise<ServerSession> {
    const answer = await call(bridge, link, 'GET', '/api/session');
    if (answer.status !== 200) {
        throw errorOf(answer, 'Asking the server who this is failed');
    }

    return JSON.parse(answer.text) as ServerSession;
}

/** Signs this device out: the server unpairs it, and a browser's cookie is cleared. */
export async function signOut(bridge: Bridge, link: ServerLink) : Promise<void> {
    const answer = await call(bridge, link, 'POST', '/api/signout');
    if (answer.status !== 200) {
        throw errorOf(answer, 'Signing out failed');
    }
}

const strings = (value: unknown) : string[] => (Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : []);

/** Whether a server of this kind is Tilez Cloud: `tilez-cloud`, or `casa-cloud` before the name Tilez (2026-11-04). */
export const isCloudKind = (kind: string | undefined) : boolean => kind === 'tilez-cloud' || kind === 'casa-cloud';

/**
 * What a server is and offers (CS-3), from its link or, before pairing, its address. Undefined from
 * a server that does not say: one older than this, or not a Tilez Hub. Thrown when it cannot be reached.
 */
export async function readServerInfo(bridge: Bridge, server: ServerLink | string) : Promise<ServerInfo | undefined> {
    const link: ServerLink = typeof server === 'string' ? { kind: 'app', url: new URL(server.trim()).origin, token: '' } : server;
    const answer = await call(bridge, link, 'GET', '/api/server');
    if (answer.status !== 200) {
        return undefined;
    }

    try {
        const parsed = JSON.parse(answer.text) as Partial<Record<keyof ServerInfo, unknown>>;
        if (typeof parsed.kind !== 'string') {
            return undefined;
        }

        return { kind: parsed.kind, version: typeof parsed.version === 'string' ? parsed.version : '', offers: strings(parsed.offers), signIn: strings(parsed.signIn) };
    } catch {
        return undefined;
    }
}

/**
 * The names the kinds are shown by (casa-server CS-3), with those servers said before the name Tilez
 * (2026-11-04), and before the split (2026-10-30).
 */
const KIND_NAMES: Record<string, string> = {
    'tilez-pico': 'Tilez Pico',
    'tilez-hub': 'Tilez Hub',
    'tilez-cloud': 'Tilez Cloud',
    'casa-pico': 'Tilez Pico',
    'casa-node': 'Tilez Hub',
    'casa-cloud': 'Tilez Cloud',
    'tiny-store': 'Tilez Pico',
    'local-admin': 'Tilez Hub',
    'public-server': 'Tilez Cloud',
};
const OFFER_NAMES: Record<string, string> = { dashboards: 'dashboards', admin: 'admin page', secrets: 'secrets', drivers: 'drivers', bridge: 'bridge to the home network', services: 'services', updates: 'app updates' };

/** "Tilez Hub 0.1.0+4955ef3: dashboards, admin page, bridge to the home network", for Settings. */
export function describeServerInfo(info: ServerInfo) : string {
    const kind = KIND_NAMES[info.kind] ?? info.kind;
    const offers = info.offers.map((offer) => OFFER_NAMES[offer] ?? offer).join(', ');

    return `${kind}${info.version ? ` ${info.version}` : ''}${offers ? `: ${offers}` : ''}`;
}

export async function listServerDashboards(bridge: Bridge, link: ServerLink) : Promise<ServerDashboard[]> {
    const answer = await call(bridge, link, 'GET', '/api/dashboards');
    if (answer.status !== 200) {
        throw errorOf(answer, 'Listing the dashboards failed');
    }

    return (JSON.parse(answer.text) as { dashboards: ServerDashboard[] }).dashboards;
}

export async function readServerDashboard(bridge: Bridge, link: ServerLink, name: string) : Promise<{ text: string; version: string }> {
    const answer = await call(bridge, link, 'GET', pathOf(name));
    if (answer.status !== 200) {
        throw errorOf(answer, `Reading "${name}" failed`);
    }

    // On tilez-cloud, the credentials sealed apart are opened, when this device can (secrets SE-10).
    return { text: await openCloudSecrets(bridge, link, name, answer.text), version: versionOf(answer.etag) };
}

/** Over the version this device had, as a new dashboard, or over whatever the server has. */
export type WriteCondition = { version: string } | 'new' | 'overwrite';

export type WriteResult = { ok: true; version: string } | { ok: false; current: string | undefined };

/** Saves a dashboard on the server. A save over a version other than `condition` is refused, not thrown (S-5). */
export async function writeServerDashboard(bridge: Bridge, link: ServerLink, name: string, text: string, condition: WriteCondition) : Promise<WriteResult> {
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    if (condition === 'new') {
        headers['if-none-match'] = '*';
    } else {
        headers['if-match'] = condition === 'overwrite' ? '*' : `"${condition.version}"`;
    }

    // On tilez-cloud, the credentials are sealed and saved apart first (secrets SE-10).
    const answer = await call(bridge, link, 'PUT', pathOf(name), await sealCloudSecrets(bridge, link, name, text), headers);
    if (answer.status === 412) {
        const { version } = JSON.parse(answer.text) as { version?: string };

        return { ok: false, current: version };
    }
    if (answer.status !== 200 && answer.status !== 201) {
        throw errorOf(answer, `Saving "${name}" failed`);
    }

    return { ok: true, version: (JSON.parse(answer.text) as { version: string }).version };
}

/** Why a delete, a rename or a duplicate was refused (docs/specs/server-admin.md, A-5). */
export type ChangeResult = { ok: true } | { ok: false; why: 'changed' | 'missing' | 'taken' };

function changeResultOf(answer: Answer, doing: string) : ChangeResult {
    switch (answer.status) {
        case 200:
        case 201:
            return { ok: true };
        case 404:
            return { ok: false, why: 'missing' };
        case 409:
            return { ok: false, why: 'taken' };
        case 412:
            return { ok: false, why: 'changed' };
        default:
            throw errorOf(answer, doing);
    }
}

/** Deletes a dashboard from the server if it is still at `version`. */
export async function deleteServerDashboard(bridge: Bridge, link: ServerLink, name: string, version: string) : Promise<ChangeResult> {
    return changeResultOf(await call(bridge, link, 'DELETE', pathOf(name), undefined, { 'if-match': `"${version}"` }), `Deleting "${name}" failed`);
}

/** Gives a dashboard on the server another name, if it is still at `version` and the name is free. */
export async function renameServerDashboard(bridge: Bridge, link: ServerLink, name: string, to: string, version: string) : Promise<ChangeResult> {
    const answer = await call(bridge, link, 'POST', `${pathOf(name)}/rename`, JSON.stringify({ to }), { 'content-type': 'application/json', 'if-match': `"${version}"` });

    return changeResultOf(answer, `Renaming "${name}" failed`);
}

/** Copies a dashboard on the server under a free name, if it is still at `version` (casa-server CS-7). */
export async function duplicateServerDashboard(bridge: Bridge, link: ServerLink, name: string, to: string, version: string) : Promise<ChangeResult> {
    const answer = await call(bridge, link, 'POST', `${pathOf(name)}/duplicate`, JSON.stringify({ to }), { 'content-type': 'application/json', 'if-match': `"${version}"` });

    return changeResultOf(answer, `Duplicating "${name}" failed`);
}

/**
 * Brings a dashboard on the server to the current schema version, if it is still at `version`
 * (server-admin A-12). A dashboard that cannot be migrated is thrown, with the server's issues.
 */
export async function migrateServerDashboard(bridge: Bridge, link: ServerLink, name: string, version: string) : Promise<ChangeResult> {
    return changeResultOf(await call(bridge, link, 'POST', `${pathOf(name)}/migrate`, undefined, { 'if-match': `"${version}"` }), `Migrating "${name}" failed`);
}

/** A browser or an app paired with the server. */
export interface PairedClient {
    id: string;
    name: string;
    kind: 'browser' | 'app';
    /** What it may do (casa-server CS-24): an admin everything, a reader its dashboards. Absent from a server before roles. */
    role?: 'admin' | 'reader';
    /** The user it signed in as; absent for a device paired with a code. */
    user?: string;
    paired: string;
    lastSeen: string;
}

/** The paired browsers and apps, which of them this device is, and whether the server pairs without a code or has users. */
export async function listPairedClients(bridge: Bridge, link: ServerLink) : Promise<{ clients: PairedClient[]; self: string | undefined; openPairing: boolean; users?: boolean }> {
    const answer = await call(bridge, link, 'GET', '/api/clients');
    if (answer.status !== 200) {
        throw errorOf(answer, 'Listing the paired devices failed');
    }

    return JSON.parse(answer.text) as { clients: PairedClient[]; self: string | undefined; openPairing: boolean; users?: boolean };
}

/** A user of a Tilez Hub with users (casa-server CS-19). */
export interface ServerUser {
    id: string;
    name: string;
    role: 'admin' | 'reader';
    created: string;
}

/** The users, and which of them this device signed in as. */
export async function listUsers(bridge: Bridge, link: ServerLink) : Promise<{ users: ServerUser[]; self: string | undefined }> {
    const answer = await call(bridge, link, 'GET', '/api/users');
    if (answer.status !== 200) {
        throw errorOf(answer, 'Listing the users failed');
    }

    return JSON.parse(answer.text) as { users: ServerUser[]; self: string | undefined };
}

async function changeUser(bridge: Bridge, link: ServerLink, method: string, path: string, body: unknown, doing: string) : Promise<void> {
    const answer = await call(bridge, link, method, path, body === undefined ? undefined : JSON.stringify(body), body === undefined ? {} : { 'content-type': 'application/json' });
    if (answer.status !== 200 && answer.status !== 201) {
        throw errorOf(answer, doing);
    }
}

const userPath = (name: string) : string => `/api/users/${encodeURIComponent(name)}`;

export const addUser = (bridge: Bridge, link: ServerLink, name: string, password: string, role: 'admin' | 'reader') : Promise<void> =>
    changeUser(bridge, link, 'POST', '/api/users', { name, password, role }, 'Making the user failed');

/** A new password; the server signs out the user's other devices. */
export const setUserPassword = (bridge: Bridge, link: ServerLink, name: string, password: string) : Promise<void> =>
    changeUser(bridge, link, 'POST', `${userPath(name)}/password`, { password }, 'Changing the password failed');

export const setUserRole = (bridge: Bridge, link: ServerLink, name: string, role: 'admin' | 'reader') : Promise<void> =>
    changeUser(bridge, link, 'POST', `${userPath(name)}/role`, { role }, 'Changing the role failed');

/** Removes a user; the server unpairs their devices. */
export const removeUser = (bridge: Bridge, link: ServerLink, name: string) : Promise<void> =>
    changeUser(bridge, link, 'DELETE', userPath(name), undefined, 'Removing the user failed');

/** Unpairs a browser or an app; the server removes what it kept for it. */
export async function unpairClient(bridge: Bridge, link: ServerLink, id: string) : Promise<void> {
    const answer = await call(bridge, link, 'DELETE', `/api/clients/${encodeURIComponent(id)}`);
    if (answer.status !== 200) {
        throw errorOf(answer, 'Unpairing failed');
    }
}

/** Makes a paired browser or app an admin or a reader; not this device itself (the server refuses that). */
export async function setClientRole(bridge: Bridge, link: ServerLink, id: string, role: 'admin' | 'reader') : Promise<void> {
    const answer = await call(bridge, link, 'POST', `/api/clients/${encodeURIComponent(id)}/role`, JSON.stringify({ role }), { 'content-type': 'application/json' });
    if (answer.status !== 200) {
        throw errorOf(answer, 'Changing the role failed');
    }
}

/** A one-time code that pairs another browser or app, and when it stops working. */
export async function makePairingCode(bridge: Bridge, link: ServerLink) : Promise<{ code: string; expires: string }> {
    const answer = await call(bridge, link, 'POST', '/api/codes');
    if (answer.status !== 200) {
        throw errorOf(answer, 'Making a pairing code failed');
    }

    return JSON.parse(answer.text) as { code: string; expires: string };
}

/** A key that calls Public-Server's services (public-services.md SV-2); the key itself is shown only when made. */
export interface ServiceKey {
    id: string;
    name: string;
    created: string;
    lastUsed?: string;
    callsToday: number;
}

/** A service of the server, and how it is used: the weather's calls to OpenWeatherMap today. */
export interface ServiceUse {
    available: boolean;
    callsToday?: number;
    dailyCalls?: number;
    places?: number;
}

/** The keys, and the services by name (`weather`, `prices`). */
export async function listServiceKeys(bridge: Bridge, link: ServerLink) : Promise<{ keys: ServiceKey[]; services: Record<string, ServiceUse> }> {
    const answer = await call(bridge, link, 'GET', '/api/service-keys');
    if (answer.status !== 200) {
        throw errorOf(answer, 'Listing the service keys failed');
    }

    const listed = JSON.parse(answer.text) as { keys: ServiceKey[]; services?: Record<string, ServiceUse> };

    return { keys: listed.keys, services: listed.services ?? {} };
}

/** A new service key, and the key, which the server shows only this once. */
export async function makeServiceKey(bridge: Bridge, link: ServerLink, name: string) : Promise<{ serviceKey: ServiceKey; key: string }> {
    const answer = await call(bridge, link, 'POST', '/api/service-keys', JSON.stringify({ name }), { 'content-type': 'application/json' });
    if (answer.status !== 201) {
        throw errorOf(answer, 'Making the service key failed');
    }

    return JSON.parse(answer.text) as { serviceKey: ServiceKey; key: string };
}

/** The key stops working at once, for every dashboard that has it. */
export async function revokeServiceKey(bridge: Bridge, link: ServerLink, id: string) : Promise<void> {
    const answer = await call(bridge, link, 'DELETE', `/api/service-keys/${encodeURIComponent(id)}`);
    if (answer.status !== 200) {
        throw errorOf(answer, 'Revoking the service key failed');
    }
}

/** A release of the app kept on tilez-cloud (app-updates AU-18, AU-19). */
export interface AppRelease {
    build: number;
    version: string;
    bridgeLevel: number;
    schemaVersion: number;
    created: string;
    files: number;
    size: number;
}

export interface AppReleases {
    releases: AppRelease[];
    /** The build current on each channel. */
    channels: Partial<Record<'test' | 'stable', number>>;
    /** The shells' checks today, by the build they had and the shell. */
    checksToday: Record<string, Record<string, number>>;
}

export async function listAppReleases(bridge: Bridge, link: ServerLink) : Promise<AppReleases> {
    const answer = await call(bridge, link, 'GET', '/api/app/releases');
    if (answer.status !== 200) {
        throw errorOf(answer, 'Listing the app releases failed');
    }

    return JSON.parse(answer.text) as AppReleases;
}

/** Makes a release the one shells on `channel` get (AU-20). */
export async function setAppChannel(bridge: Bridge, link: ServerLink, channel: 'test' | 'stable', build: number) : Promise<void> {
    const answer = await call(bridge, link, 'PUT', `/api/app/channels/${channel}`, JSON.stringify({ build }), { 'content-type': 'application/json' });
    if (answer.status !== 200) {
        throw errorOf(answer, 'Setting the channel failed');
    }
}
